import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  YAxis,
  ResponsiveContainer,
} from 'recharts';
import { Box, Typography, useTheme, Grid, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { IClientProgressData } from '../../redux/client/clientSlice';
import RenderLegend from './Legend';
import { useAppSelector } from '../../redux/hooks';
import { CircularSkeleton, RoundedSkeleton } from '../ClientTable/LoadingSkeleton';
import i18n from '../../i18n';

interface IDataEntry {
  createdAt: string;
  [key: string]: string | number;
}

export const formatNumberToStringMonthWithoutYear = (inputDate?: string) => {
  const months = [
    i18n.t('common.monthsShort.jan'),
    i18n.t('common.monthsShort.feb'),
    i18n.t('common.monthsShort.mar'),
    i18n.t('common.monthsShort.apr'),
    i18n.t('common.monthsShort.may'),
    i18n.t('common.monthsShort.jun'),
    i18n.t('common.monthsShort.jul'),
    i18n.t('common.monthsShort.aug'),
    i18n.t('common.monthsShort.sep'),
    i18n.t('common.monthsShort.oct'),
    i18n.t('common.monthsShort.nov'),
    i18n.t('common.monthsShort.dec'),
  ];

  const date = inputDate ? new Date(inputDate) : new Date();

  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];

  return `${month} ${day}`;
};

interface IProgress {
  createdAt: string;
  scale: number;
}

interface IObjectiveProgress {
  progressList: IProgress[];
  objectiveName: string;
}

const reformatData = (data: IObjectiveProgress[]): IDataEntry[] => {
  const formattedData: IDataEntry[] = [];
  // Iterate over each progress entry
  data.forEach((progressGroup) => {
    progressGroup.progressList.forEach((progress) => {
      const formattedDate = formatNumberToStringMonthWithoutYear(progress.createdAt);
      // Find or create the entry for the formatted date
      let formattedDataItem = formattedData.find((item) => item.createdAt === formattedDate);
      if (!formattedDataItem) {
        formattedDataItem = { createdAt: formattedDate };
        formattedData.push(formattedDataItem);
      }
      // Set the scale for the objective
      formattedDataItem[progressGroup.objectiveName] = progress.scale;
    });
  });
  return formattedData.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });
};

interface ObjectivesChartProps {
  objectiveProgress: IClientProgressData[];
}

function ObjectivesChart({ objectiveProgress }: ObjectivesChartProps) {
  const [data, setData] = useState<IDataEntry[]>([]);
  const theme = useTheme();
  const { t } = useTranslation();
  const clientActivityProgressLoading = useAppSelector(
    (state) => state.client.clientActivityProgressLoading
  );

  useEffect(() => {
    if (objectiveProgress.length === 0) return;
    setData(reformatData(objectiveProgress));
  }, [objectiveProgress]);

  if (clientActivityProgressLoading) {
    return (
      <Grid gap={4}>
        <Skeleton
          variant="rounded"
          width="100%"
          height={270}
          animation="wave"
          sx={{
            borderRadius: '54px',
            background: 'linear-gradient(270deg, rgba(219, 219, 219, 0.05) 0%, #DBDBDB 50%)',
          }}
        />
        <Grid container spacing={0} rowGap="12px" mt={2}>
          {[...Array(6)].map((_count) => (
            <Grid item xs={6} key={uuidv4()} display="flex" alignItems="center" gap={1}>
              <CircularSkeleton variant="circular" width={16} height={16} />
              <RoundedSkeleton variant="rounded" width={136} height={14.875} />
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  }

  if (data.length === 0 && !clientActivityProgressLoading)
    return (
      <Box width="100%" height="265px" display="flex" alignItems="center" justifyContent="center">
        <Typography variant="bodyBold" color="rgba(42, 60, 103, 0.20)">
          {t('clientProgress.noData')}
        </Typography>
      </Box>
    );

  return (
    <ResponsiveContainer width="100%" height={360}>
      <LineChart data={data} margin={{ left: 0, top: 5, right: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="createdAt"
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#2A3C67' }}
          style={{
            color: theme.palette.primary.main,
            textShadow: '0px 4px 32px rgba(0, 0, 0, 0.07)',
            fontFamily: 'Satoshi',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '120%',
          }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={40}
          tick={{ fill: '#2A3C67' }}
          style={{
            color: theme.palette.primary.main,
            textAlign: 'right',
            textShadow: '0px 4px 32px rgba(0, 0, 0, 0.07)',
            fontFamily: 'Satoshi',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '120%',
          }}
        />
        <Tooltip />
        <Legend content={<RenderLegend objectiveProgress={objectiveProgress} />} />
        {objectiveProgress.map((objective) => (
          <Line
            type="monotone"
            dataKey={objective.objectiveName}
            stroke={objective.colorCode}
            key={objective.objectiveId}
            isAnimationActive={false}
            strokeWidth={3}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default ObjectivesChart;
